import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { storage } from '@/lib/storage';

/**
 * DataContext.
 *
 * Centraliza el estado de quotes y discount codes.
 * Todas las mutaciones pasan por aquí para mantener el estado sincronizado
 * sin importar si la fuente es localStorage o la API.
 */

const DataContext = createContext(null);

export function DataProvider({ children }) {
  const [quotes, setQuotes] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [q, c] = await Promise.all([
        storage.listQuotes(),
        storage.listDiscountCodes(),
      ]);
      setQuotes(q || []);
      setDiscountCodes(c || []);
    } catch (e) {
      console.error('Error cargando datos:', e);
      setError(e.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const saveQuote = async (quote) => {
    const saved = await storage.saveQuote(quote);
    setQuotes((prev) => {
      const idx = prev.findIndex((q) => q.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [...prev, saved];
    });
    return saved;
  };

  const deleteQuote = async (id) => {
    await storage.deleteQuote(id);
    setQuotes((prev) => prev.filter((q) => q.id !== id));
  };

  const updateDiscountCodes = async (newCodes) => {
    const saved = await storage.saveDiscountCodes(newCodes);
    setDiscountCodes(saved || newCodes);
  };

  const nextFolio = () => storage.nextFolio();

  return (
    <DataContext.Provider
      value={{
        quotes,
        discountCodes,
        loading,
        error,
        refresh,
        saveQuote,
        deleteQuote,
        updateDiscountCodes,
        nextFolio,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export const useData = () => {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData debe usarse dentro de DataProvider');
  return ctx;
};
